import { APIFieldAttributes, APIFieldType } from '@ardoq/api-types';
import { validateAs } from './validationStep';
import { ValidationDescriptor } from './types';

/**
 * This validates a component/reference attribute value given
 * the field and locale of the user.
 */
export const getValidatorDescriptorByField = (data: {
  field: APIFieldAttributes;
  value: any;
}): ValidationDescriptor => {
  const { field, value } = data;

  switch (field.type) {
    case APIFieldType.DATE_ONLY:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps: [validateAs.dateOnlyISO8601()],
      };
    case APIFieldType.DATE_TIME:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps: [validateAs.dateTimeISO8601()],
      };
    case APIFieldType.DATE_ONLY_RANGE:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps: [validateAs.dateOnlyRange()],
      };
    case APIFieldType.DATE_TIME_RANGE:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps: [validateAs.dateTimeRange()],
      };
    case APIFieldType.NUMBER:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps: [validateAs.number(), validateAs.numberSafeInteger()],
      };
    case APIFieldType.EMAIL:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps: [validateAs.email()],
      };
    case APIFieldType.LIST:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps:
          field.defaultValue &&
          typeof field.defaultValue === 'string' &&
          field.defaultValue.trim()
            ? [validateAs.includedIn({ array: field.defaultValue.split(',') })]
            : [],
      };
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return {
        value,
        allowedValues: [null, undefined, []],
        validationSteps:
          field.defaultValue &&
          typeof field.defaultValue === 'string' &&
          field.defaultValue.trim()
            ? [validateAs.subsetOf({ array: field.defaultValue.split(',') })]
            : [],
      };
    case APIFieldType.CHECKBOX:
      return {
        value,
        allowedValues: [null, undefined],
        validationSteps: [validateAs.includedIn({ array: [true, false] })],
      };
    default:
      return { value, validationSteps: [] };
  }
};

export const getTagNameValidatorDescriptor = (
  value: unknown
): ValidationDescriptor => ({
  value,
  validationSteps: [validateAs.tagName()],
});

export const getTagNamesValidatorDescriptor = (
  value: unknown
): ValidationDescriptor => ({
  value,
  allowedValues: [null, undefined],
  validationSteps: [validateAs.everyTagName()],
});

export const getPasswordValidatorDescriptor = (value: any) => {
  return {
    value,
    validationSteps: [
      validateAs.requiredString(),
      {
        ...validateAs.minLength({ length: 12 }),
        message: `Minimum password length is 12, you have ${value?.length}`,
      },
      validateAs.passwordComplexity(),
    ],
  };
};
